import React, { useState, useEffect, useContext } from 'react'
import axios from 'axios'
import { Box, CircularProgress, Typography } from '@mui/material'
import { LocalizationContext } from '../../../shared/contexts'
import { formatTenantTime } from '../../../../utils/formatTenantDateTime'
import DosingDifferencePercent from '../../../shared/DosingDifferencePercent'
import { weightUnit } from '../../../shared/weightScale'
import { TIME_SOURCE, timeSourceTitle } from '../../../../utils/timeSource'

interface DosingRow {
  id: number
  product_name: string | null
  container_slot_number: number | null
  container_id: number | null
  planned_amount: number
  actual_dosed: number
  // computed once per dosing operation, on its final row; null on the partial
  // attempt rows of a dose split across containers
  difference_percent: number | null
  amount_left: number | null
  executed_at: string | null
  dosing_failed: boolean
}

const DosingSection = ({ basePath }: { basePath: string }) => {
  const [rows, setRows] = useState<DosingRow[] | null>(null)
  const [loading, setLoading] = useState(true)
  const localization = useContext(LocalizationContext)
  const { weight_scale: weightScale } = localization
  const formatTime = (value: string | null) => formatTenantTime(value, localization)

  useEffect(() => {
    axios.get(`${basePath}/dosing_amounts`)
      .then(({ data }) => {
        setRows(data)
        setLoading(false)
      })
      .catch(() => setLoading(false))
  }, [basePath])

  if (loading) {
    return <Box className='text-center py-3'><CircularProgress size={24} /></Box>
  }

  if (!rows || rows.length === 0) {
    return <Typography variant='body2' color='text.secondary'>No dosing data recorded for this order.</Typography>
  }

  const unit = weightUnit(weightScale)

  return (
    <table className='table table-robis order-show-dosing-table'>
      <thead className='thead-robis'>
        <tr>
          <th>Product</th>
          <th>Container</th>
          <th>Planned ({unit})</th>
          <th>Actual ({unit})</th>
          <th>Difference (%)</th>
          <th>Left before dose ({unit})</th>
          <th>Result</th>
          <th>Executed at</th>
        </tr>
      </thead>
      <tbody>
        {rows.map((row, idx) => (
          <tr key={`${row.id}-${idx}`}>
            <td>{row.product_name || '—'}</td>
            <td>
              {row.container_slot_number != null
                ? `#${row.container_slot_number}${row.container_id ? ` (ID: ${row.container_id})` : ''}`
                : '—'}
            </td>
            <td>{row.planned_amount}</td>
            <td>{row.actual_dosed}</td>
            <td><DosingDifferencePercent percent={row.difference_percent} placeholder='—' /></td>
            <td>{row.amount_left != null ? row.amount_left : '—'}</td>
            <td>{row.dosing_failed ? <span className='text-danger'>Failed</span> : 'Success'}</td>
            <td title={timeSourceTitle(row.executed_at, TIME_SOURCE.robot)}>{formatTime(row.executed_at) || '—'}</td>
          </tr>
        ))}
      </tbody>
    </table>
  )
}

export default DosingSection
